import React, { useMemo } from "react";
import {
  Typography as MuiTypography,
  Divider as MuiDivider,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Box,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";

import useQueryParams from "../../hooks/useQueryParams";
import useFetchGraphData from "../../hooks/useFetchGraphData";
import useUsdsBoxplotSeries from "../../hooks/charts/useUsdsBoxplotSeries";
import useUsdsAnnualSeries from "../../hooks/charts/useUsdsAnnualSeries";
import FiltersForm from "../../components/FiltersForm";
import RawTable from "../../components/RawTable";
import UsdsBoxPlotChartContainer from "../../components/charts/usdsBoxPlot/UsdsBoxPlotChartContainer";
import UsdsLineChartContainer from "../../components/charts/usdsLineChart/UsdsLineChartContainer";
import {
  COMPARISON_PERIODS,
  FLOW_CONDITIONS,
  STATISTICS,
  VIEWS,
  inOptions,
  isConcentration,
  periodLabel,
  toApiQuery,
  usdsSchema,
} from "./usdsParams";
import {
  landmarksForReach,
  profileMouths,
} from "../../hooks/charts/useUsdsAnnualSeries";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

/**
 * Builds the filter config, which FiltersForm renders and writes straight back
 * into the URL.
 */
export const buildFilters = (
  params,
  parameters,
  waterYears,
  reaches,
  resolvedYear
) => {
  const parameterOptions = parameters.map((p) => ({
    label: p.param_descrip,
    value: String(p.parameterid),
  }));
  const yearOptions = waterYears.map((y) => ({
    label: `WY ${y.water_year}`,
    value: String(y.water_year),
  }));
  const reachOptions = reaches.map((r) => ({
    label: r.reach_label,
    value: String(r.reach_groupid),
  }));
  return [
    {
      type: "select",
      param: "parameterid",
      label: "Parameter",
      default: inOptions(params.parameterid, parameterOptions),
      options: parameterOptions,
    },
    {
      type: "select",
      param: "year",
      label: "Water year",
      default: inOptions(resolvedYear, yearOptions),
      options: yearOptions,
    },
    {
      type: "select",
      param: "comparison",
      label: "Compare with",
      default: String(params.comparison),
      options: COMPARISON_PERIODS.map(({ label, value }) => ({
        label,
        value: String(value),
      })),
    },
    {
      type: "select",
      param: "flow",
      label: "Flow condition",
      default: params.flow,
      options: FLOW_CONDITIONS.map(({ label, value }) => ({ label, value })),
    },
    {
      type: "select",
      param: "reaches",
      label: "Reaches",
      multi: true,
      default: inOptions(params.reaches, reachOptions),
      options: reachOptions,
    },
    {
      type: "select",
      param: "view",
      label: "View",
      default: params.view,
      options: VIEWS.map(({ label, value }) => ({ label, value })),
    },
  ];
};

export default function UpstreamDownstream() {
  /* The page level table shows one row per site and period, the same rows the
     line view draws. material-table renders a column through `render` when the
     raw field would not read well on its own. */
  const RAW_COLUMNS = [
    { title: "Reach", field: "reach_label" },
    { title: "Site", field: "location_name" },
    { title: "Parameter", field: "param_abbrev" },
    {
      title: "Period",
      field: "collect_year",
      render: (row) =>
        periodLabel(row.collect_year, row.water_year_min, row.water_year_max),
    },
    { title: "Flow", field: "flow_condition" },
    { title: "n", field: "recordcount", type: "numeric" },
    { title: "Median", field: "median", type: "numeric" },
    { title: "Mean", field: "meanval", type: "numeric" },
    { title: "Min", field: "minval", type: "numeric" },
    { title: "Max", field: "maxval", type: "numeric" },
    { title: "Units", field: "unit_name" },
  ];

  const { params, setParams } = useQueryParams(usdsSchema);

  const { data: parameters = [] } = useFetchGraphData({}, "usds/parameters");
  const { data: reaches = [] } = useFetchGraphData({}, "usds/reaches");
  const { data: landmarks = [] } = useFetchGraphData({}, "usds/landmarks");
  const { data: waterYears = [] } = useFetchGraphData(
    { parameterid: params.parameterid, flow: params.flow },
    "usds/water-years"
  );

  /* The water year defaults to the most recent one with data for the chosen
     parameter, never to today's date. Until that list arrives the year stays
     unresolved and the chart hooks wait rather than fetch an empty year. */
  const resolvedYear = useMemo(() => {
    const available = waterYears.map((y) => y.water_year);
    /* A year chosen for one parameter may not exist for the next; draw the
       latest year that does rather than an empty chart. While the list is
       still loading the URL's year stands, so the first fetch is not delayed. */
    if (
      Number.isInteger(params.year) &&
      (!available.length || available.includes(params.year))
    )
      return params.year;
    if (available.length) return Math.max(...available);
    return null;
  }, [params.year, waterYears]);

  const boxplot = useUsdsBoxplotSeries(params, resolvedYear);
  const annual = useUsdsAnnualSeries(params, resolvedYear);

  const isLine = params.view === "line";
  const active = isLine ? annual : boxplot;

  const selectedLabel = periodLabel(resolvedYear, resolvedYear, resolvedYear);
  const statisticLabel =
    STATISTICS.find((s) => s.value === params.statistic)?.label ?? "Median";

  const filters = useMemo(
    () => buildFilters(params, parameters, waterYears, reaches, resolvedYear),
    [params, parameters, waterYears, reaches, resolvedYear]
  );

  /* The line view adds a statistic picker and an optional second parameter. */
  const lineFilters = useMemo(
    () =>
      isLine
        ? [
            {
              type: "select",
              param: "statistic",
              label: "Statistic",
              default: params.statistic,
              options: STATISTICS.map(({ label, value }) => ({ label, value })),
            },
            {
              type: "select",
              param: "parameter2",
              label: "Second parameter (right axis)",
              default: inOptions(params.parameter2, [
                ...parameters.map((p) => ({ value: String(p.parameterid) })),
              ]),
              options: [
                { label: "None", value: "" },
                ...parameters
                  .filter((p) => p.parameterid !== params.parameterid)
                  .map((p) => ({
                    label: p.param_descrip,
                    value: String(p.parameterid),
                  })),
              ],
            },
          ]
        : [],
    [
      isLine,
      params.statistic,
      params.parameter2,
      params.parameterid,
      parameters,
    ]
  );

  const domain = params.lockYAxis ? boxplot.lockedDomain : null;
  const unitName = parameters.find(
    (p) => p.parameterid === params.parameterid
  )?.unit_name;
  const offerLog = isConcentration(unitName);

  const rawParams = Number.isInteger(resolvedYear)
    ? {
        ...toApiQuery(params, resolvedYear),
        ...(params.parameter2 ? { parameter2: params.parameter2 } : {}),
      }
    : null;

  return (
    <>
      <Helmet title="Upstream to Downstream" />
      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          Upstream to Downstream
        </Typography>
        <Divider my={4} />
      </header>

      <main>
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            This tool follows a water quality parameter down a stream reach.
            Each monitoring site sits on the x-axis in{" "}
            <strong>upstream-to-downstream order</strong>, so a rise or fall
            across the chart is a change <strong>along the stream</strong>, not
            a change over time. Cherry Creek, Cottonwood Creek and McMurdo Gulch
            are shown by default; the other reaches can be added from the
            filters.
          </Typography>
          <Typography variant="body1" gutterBottom>
            At each site a <strong>dark box</strong> covers the water year you
            select and a <strong>light box</strong> beside it covers the
            comparison period (the last 5 water years, the last 10, or all prior
            years), which gives a single year something to be read against.
            Switch to the <strong>line chart</strong> to plot one statistic per
            site instead, with an optional second parameter on the right axis.
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Tip:</strong> As you adjust the filters, the charts and
            table update automatically. The page URL updates too, so you can{" "}
            <strong>bookmark</strong> or
            <strong> share a direct link</strong> to your current view.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <FiltersForm
            config={[...filters, ...lineFilters]}
            onChange={setParams}
          />
          {!isLine && (
            <FormControlLabel
              control={
                <Switch
                  checked={params.lockYAxis}
                  onChange={(e) =>
                    setParams({ ...params, lockYAxis: e.target.checked })
                  }
                  color="primary"
                />
              }
              label="Use one y-axis across every reach"
            />
          )}
          {offerLog && (
            <FormControlLabel
              control={
                <Switch
                  checked={params.scale === "log"}
                  onChange={(e) =>
                    setParams({
                      ...params,
                      scale: e.target.checked ? "log" : "linear",
                    })
                  }
                  color="primary"
                />
              }
              label="Log scale"
            />
          )}
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Graph
          </Typography>

          {active.isLoading && !active.reaches.length && (
            <Box display="flex" justifyContent="center" p={6}>
              <CircularProgress />
            </Box>
          )}

          {active.isError && (
            <Typography variant="body1" color="error">
              The charts could not be loaded. Please try again.
            </Typography>
          )}

          {!active.isLoading && !active.isError && !active.reaches.length && (
            <Typography variant="body1">
              No results for this parameter, water year and flow condition.
            </Typography>
          )}

          <Grid container spacing={6}>
            {active.reaches.map((reach) => (
              <Grid item xs={12} key={reach.reachGroupid}>
                {isLine ? (
                  <UsdsLineChartContainer
                    reach={reach}
                    statisticLabel={statisticLabel}
                    scale={params.scale}
                    landmarks={landmarksForReach(landmarks, reach.reachGroupid)}
                    mouths={profileMouths(
                      landmarksForReach(landmarks, reach.reachGroupid),
                      annual.allReaches
                    )}
                  />
                ) : (
                  <UsdsBoxPlotChartContainer
                    reach={reach}
                    parameter={boxplot.parameter}
                    selectedLabel={selectedLabel}
                    domain={domain}
                    landmarks={landmarksForReach(landmarks, reach.reachGroupid)}
                    scale={params.scale}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Graph
          </Typography>

          <Typography variant="body1" gutterBottom>
            Each <strong>box</strong> spans the middle half of the results at a
            site: its bottom edge is the <strong>25th percentile</strong> and
            its top edge the <strong>75th</strong>. The line inside is the{" "}
            <strong>median</strong>, used rather than the average because a
            single storm sample can pull an average a long way. The{" "}
            <strong>whiskers</strong> reach the 5th and 95th percentiles, so
            they span the bulk of the record without being dragged out by the
            most extreme sample. Hover a box for its count, date range and
            statistics.
          </Typography>

          <Typography variant="body1" gutterBottom>
            The number above each box is its <strong>sample count</strong>. A
            box built from fewer than ten values is also drawn over the samples
            themselves, so two or three points are never mistaken for a
            distribution. <strong>Non-detects</strong> are stored and plotted at
            zero and drawn hollow; a box that is mostly non-detects is marked{" "}
            <strong>ND</strong>, because it describes the detection limit more
            than the stream.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Shaded bands mark what lies <strong>between</strong> two sites: the
            pollutant reduction facilities on Cottonwood Creek (Peoria Pond, the
            Perimeter System Wetland) and McMurdo Gulch, and the reservoir on
            Cherry Creek. A change from the site before a band to the site after
            it is a change across that feature. A{" "}
            <strong>dashed vertical line</strong> is a point rather than a
            stretch: where a tributary joins, so downstream of it the stream
            also carries that tributary's water. Each chart's legend keys the
            marks it shows.
          </Typography>

          <Typography variant="body1" gutterBottom>
            A dashed red <strong>benchmark line</strong> is drawn where the
            parameter has one, and the legend above each chart gives its value.
            For most parameters it marks the value above which results are
            considered above benchmark; for dissolved oxygen it marks the value
            below which they are. When the benchmark lies far outside the data
            the line is left off so the boxes stay readable, and the legend says
            so.
          </Typography>

          <Typography variant="body1" gutterBottom>
            The comparison box is labeled with the{" "}
            <strong>water years the period covers</strong>, anchored to the most
            recent water year with data rather than to today's date. A site
            whose record starts later than that window covers less of it; its
            own coverage is shown in the tooltip and in the data table. A water
            year runs from 1 October to 30 September and is named for the
            calendar year it ends in, so water year 2025 covers October 2024
            through September 2025.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Some sites are visited only on the spring and fall{" "}
            <strong>synoptic</strong> runs. A single-year box there rests on two
            or three points and says little on its own; read the comparison box
            instead. Those sites are named under each chart.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Each reach is drawn on its own y-axis by default, because the
            reaches differ by an order of magnitude and one shared axis flattens
            the smaller ones. Turn on{" "}
            <strong>Use one y-axis across every reach</strong> when the question
            is which reach is higher rather than what happens along it. Click a
            legend item to hide or show that period or the benchmark line;
            unless the axes are locked, the axis then resizes to what is left.
          </Typography>

          <Typography variant="body1" gutterBottom>
            For concentrations, <strong>Log scale</strong> spreads out values
            that span orders of magnitude, such as nutrients between baseflow
            and storms, and keeps a low reach readable beside a high one when
            the axes are locked. It is not offered for temperature, pH, oxygen
            saturation or redox potential, where a log axis would mislead.
          </Typography>

          <Typography variant="body1" gutterBottom>
            On the line view, Cherry Creek is drawn as a{" "}
            <strong>profile by stream mile</strong> from Franktown, with the
            tributaries entering as triangles at their confluences, each
            carrying the value from its lowest site, and the reservoir as a band
            between the inflow and the outlet. Three mainstem positions and the
            confluence miles are estimated from latitude until they are
            measured; those points are drawn hollow and named under the chart.
            The tributaries themselves are short enough that their sites are
            spaced evenly, in order.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Values are <strong>daily averages of surface samples</strong>,
            unit-converted, from the same source the interactive map uses for
            its statistics. Site order comes from the historic stream distances
            where those exist and from latitude or longitude elsewhere. Not
            every reach carries a full set of sites: McMurdo Gulch is drawn from
            MCM-1 and MCM-2, the two sites still sampled there; Shop Creek has
            no results after water year 2014; and Piney Creek has a single site,
            PC-1, so it shows a distribution rather than a gradient.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Raw Data Table
          </Typography>
          {/* Fresh copies: material-table mutates the columns it is given.
              main's RawTable copies internally since 4313d33; this branch
              predates that, and double-copying after the rebase is harmless. */}
          {rawParams ? (
            <RawTable
              endPoint="usds/annual"
              params={rawParams}
              columns={RAW_COLUMNS.map((column) => ({ ...column }))}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Loading water years...
            </Typography>
          )}
        </Section>
      </main>
    </>
  );
}
